import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatShortDate } from "@/lib/formatters";
import {
  listJobs,
  deleteJob as apiDeleteJob,
  getJobStatus,
} from "@/lib/api-client";
import { toast } from "sonner";
import { parseApiError } from "@/lib/api-error";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { List, Eye, Trash2, PlusCircle, Calendar } from "lucide-react";
import type { SearchJob } from "@/types/api";

function timeAgo(dateStr: string) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function SearchJobsPage() {
  const { t } = useTranslation("search");
  const [jobs, setJobs] = useState<SearchJob[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pollRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const fetchJobs = useCallback(async () => {
    try {
      const data = await listJobs();
      setJobs(data);
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Polling for pending/running jobs
  useEffect(() => {
    const activeJobs = jobs.filter(
      (j) => j.status === "pending" || j.status === "running",
    );

    Object.keys(pollRef.current).forEach((id) => {
      if (!activeJobs.find((j) => j.job_id === id)) {
        clearInterval(pollRef.current[id]);
        delete pollRef.current[id];
      }
    });

    activeJobs.forEach((job) => {
      if (pollRef.current[job.job_id]) return;

      pollRef.current[job.job_id] = setInterval(async () => {
        try {
          const updated = await getJobStatus(job.job_id);
          if (updated.status !== job.status) {
            fetchJobs();
          }
          if (updated.status === "completed" || updated.status === "failed") {
            clearInterval(pollRef.current[job.job_id]);
            delete pollRef.current[job.job_id];
          }
        } catch {
          // Silently ignore polling errors
        }
      }, 3000);
    });

    return () => {
      Object.values(pollRef.current).forEach(clearInterval);
      pollRef.current = {};
    };
  }, [jobs, fetchJobs]);

  const handleDelete = async (jobId: string) => {
    try {
      await apiDeleteJob(jobId);
      setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
      toast.success(t("jobsList.actions.jobDeleted"));
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <List className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t("jobsList.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("jobsList.jobCount", { count: jobs.length })}
            </p>
          </div>
        </div>
        <Button onClick={() => navigate("/search/new")}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t("jobsList.newSearch")}
        </Button>
      </div>

      {jobs.length === 0 ? (
        <EmptyState
          icon={List}
          title={t("jobsList.emptyState.title")}
          description={t("jobsList.emptyState.description")}
          action={
            <Button onClick={() => navigate("/search/new")}>
              <PlusCircle className="mr-2 h-4 w-4" />
              {t("jobsList.newSearch")}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <Card
              key={job.job_id}
              className="p-5 shadow-card hover:shadow-md transition-all duration-150"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">
                      {[job.request.industry, job.request.location]
                        .filter(Boolean)
                        .join(" · ") || t("jobsList.untitledSearch")}
                    </span>
                    <StatusBadge status={job.status} />
                    {job.meta.total_results != null &&
                      job.status === "completed" && (
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          {t("jobsList.results", {
                            count: job.meta.total_results,
                          })}
                        </span>
                      )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatShortDate(job.created_at)}
                    <span className="text-muted-foreground/60">
                      ({timeAgo(job.created_at)})
                    </span>
                    {job.request.employee_size &&
                    job.request.employee_size !== "any"
                      ? ` · ${t("jobsList.employees", { size: job.request.employee_size })}`
                      : ""}
                    {job.request.keywords && job.request.keywords.length > 0
                      ? ` · ${job.request.keywords.join(", ")}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {job.status === "completed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/jobs/${job.job_id}/results`)}
                    >
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                      {t("jobsList.actions.results")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(job.job_id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
